import {
  findEndCardIds,
  findPinnedFollowerIds,
} from '@src/editor/lib/placementBounds';
import { Element } from '@src/editor/models/Element';
import { CompositionElement } from '@src/editor/models/elements/CompositionElement';
import { isIntroCardMeta } from '@src/utility/extraElementData';

import type { Track } from '@src/editor/models/Track';

/**
 * The reel-level elements no Section owns, derived from the timeline itself.
 *
 * `reelSectionContents` takes these as `excludeElementIds` and documents why
 * they cannot be Section contents (DE7): they are pinned to the reel's own
 * start or end rather than to a passage, so carrying one with the passage that
 * happens to contain its start moves it somewhere it was never placed — and on
 * a removal deletes it outright.
 *
 * That set was originally meant to come from the video template settings, which
 * are store state. Deriving it from the timeline instead means every reel
 * command gets it for free, and a clip whose template meta is missing or stale
 * — a hand-placed end card, a legacy clip — is covered all the same.
 *
 * What is exempt:
 * - every pinned composition, whose extent is derived rather than authored;
 * - every intro and end card, pinned or not, because a legacy unpinned card is
 *   still a card;
 * - every logo, coupled or not: a logo spans the reel the way music does.
 *
 * @param alsoExclude ids the caller wants exempt on top of these
 */
export function reelExemptElementIds(
  tracks: readonly Track[],
  extraData: Record<string, unknown>,
  alsoExclude?: ReadonlySet<string>,
): Set<string> {
  const ids = new Set<string>([
    ...findPinnedFollowerIds(tracks, extraData),
    ...findEndCardIds(tracks, extraData),
    ...(alsoExclude ?? []),
  ]);

  for (const track of tracks) {
    for (const element of track.elements) {
      if (Element.isLogoElement(element)) {
        ids.add(element.id);
        continue;
      }
      if (
        element instanceof CompositionElement &&
        isIntroCardMeta(extraData[element.id])
      ) {
        ids.add(element.id);
      }
    }
  }

  return ids;
}
